/**
 * The UI pane slider component to have a slider controlled programmatically.
 *
 * TODO: This is still beta while we try and untangle the passwordless form.
 * Please hold back from using this for now. Check with
 * sinatra@freelancer.com for details.
 */

import 'web-animations-js';

/**
 * Generates a unique identifier
 * @return {string}  String for the unique identifier.
 */
function generateUniqueIdentifier() {
  const identifier = `passwordless-${Math.floor(Math.random() * 1024)}`;
  // Does the passwordless form with that identifier exist? Reroll the
  // dice again to get a new unique one.
  if (document.getElementById(identifier)) {
    return generateUniqueIdentifier();
  }

  return identifier;
}

/**
 * Generates a ui-pane-slider component.
 * @param  {[type]} [identifier=generateUniqueIdentifier(] [description]
 * @param {Object} [options={}] The options for the component.
 * @param {Function} [options.beforePaneSwitchEventHandler] Event handler for
 * before the slider switches to another pane. Return false explicitly to
 * cancel it. Passes in the current pane, the pane to switch to, and all the
 * panes.
 * @param {Function} [options.afterPaneSwitchEventHandler] Event handler for
 * after the slider switches to another pane. Passes in the new pane switched
 * to, the old pane, and all the panes.
 * @return {Object}  All the public API to work with the pane slider.
 */
export default function uiPanelSliderFactory(
  identifier = generateUniqueIdentifier(),
  options = {},
) {
  const containerEl = document.getElementById(`${identifier}`);
  const panesEl = document.getElementById(`${identifier}-panes`);
  const beforePaneSwitchEventHandler =
    options.beforePaneSwitchEventHandler || (() => true);
  const afterPaneSwitchEventHandler =
    options.afterPaneSwitchEventHandler || (() => true);
  const progressOnTab =
    typeof options.progressOnTab === 'undefined' ? options.progressOnTab : true;

  const panes = Array.from(containerEl.querySelectorAll('[data-pane]'));
  const paneCount = panes.length;
  let activePane = 1;

  /**
   * Sets the first focus to the first element with `data-first-focus`.
   * @param {number} [paneNum=activePane] The pane to get the element to
   *                                      focus on.
   */
  function setFirstFocus(paneNum = activePane) {
    const pane = panesEl.querySelector(`[data-pane="${paneNum}"]`);
    if (!pane) {
      return;
    }

    const firstFocusElement = pane.querySelector('[data-first-focus]');
    if (firstFocusElement) {
      firstFocusElement.focus();
    }
  }

  /**
   * Set classes for the pane to set visibility and position.
   * @param {Node} paneEl            The pane element to set classes.
   * @param {Number} paneIndex         The pane element's index.
   * @param {Number} currentActivePane The currently active pane index.
   */
  function setPaneClassForSwitching(paneEl, paneIndex, currentActivePane) {
    if (currentActivePane === paneIndex) {
      paneEl.classList.add('is-shown');
      paneEl.classList.remove('is-past');
      // This is the pane to switch to. Focus on the first element.
      setFirstFocus(currentActivePane);
    } else if (currentActivePane > paneIndex) {
      paneEl.classList.add('is-past');
      paneEl.classList.remove('is-shown');
    } else if (currentActivePane < paneIndex) {
      paneEl.classList.remove('is-past');
      paneEl.classList.remove('is-shown');
    }
  }

  /**
   * Switches pane to the desired pane number.
   * @param  {number} pane                    Pane number to switch to.
   * @param  {boolean}   [playAnimation=true] Whether or not to play animation
   *                                          to switch to the pane.
   * @return {Animation|boolean}              Web Animation API player of the
   *                                          middle pane or `false` if
   *                                          `playAnimation` is set to `false`.
   */
  function switchPanes(pane, playAnimation = true) {
    // To make sure the horizontal scroll isn't moved always reset scrollLeft
    // to 0.
    panesEl.scrollLeft = 0;

    // Cancel pane switching if we're switching to the displayed pane anyway.
    if (activePane === pane) {
      return;
    }

    const currentPane = activePane;
    const newPane = Math.min(Math.max(pane, 1), paneCount);

    const eventNotPrevented = beforePaneSwitchEventHandler(
      currentPane,
      newPane,
      panes,
    );

    // Cancel pane switching if the event handler explicitly returned false.
    if (eventNotPrevented === false) {
      return;
    }

    activePane = newPane;

    // Take care if current pane is disabled
    if (panes[newPane - 1].hasAttribute('data-pane-disabled')) {
      // Is this switching back or next?
      if (newPane - currentPane < 0) {
        switchPanes(newPane - 1);
      } else {
        switchPanes(newPane + 1);
      }
      return;
    }

    // Animate with FLIP.
    // We're not grouping them in an array and doing a loop because we need
    // to batch the DOM calls to prevent layout thrashing.
    const startBoundings = panes.map(pane => pane.getBoundingClientRect());

    panes.forEach((pane, index) => {
      setPaneClassForSwitching(pane, index + 1, newPane);
    });

    const endBoundings = panes.map(pane => pane.getBoundingClientRect());
    const leftInverses = startBoundings.map(
      (startBoundings, i) => startBoundings.left - endBoundings[i].left,
    );

    const players = panes.map((pane, i) => {
      // If the pane is disabled, do no animation
      if (pane.hasAttribute('data-pane-disabled') || !playAnimation) {
        return false;
      }

      return pane.animate(
        [
          {
            transform: `translateX(${leftInverses[i]}px)`,
            opacity: newPane === i + 1 ? 0 : 1,
          },
          {
            transform: 'translateX(0)',
            opacity: newPane === i + 1 ? 1 : 0,
          },
        ],
        {
          duration: 500,
          easing: 'ease-in-out',
        },
      );
    });

    afterPaneSwitchEventHandler(newPane, currentPane, panes);

    return players[1];
  }

  /**
   * Returns the index of the currently active pane.
   * @return {number} The currently active pane index.
   */
  function getActivePane() {
    return activePane;
  }

  /**
   * Resets the form and switches back to the first pane.
   * @return {void}
   */
  function reset() {
    switchPanes(1);
  }

  /**
   * Switch to the next pane from the current pane.
   * @return {void}
   */
  function nextPane() {
    switchPanes(activePane + 1);
  }

  /**
   * Switch back to the previous pane from the current pane.
   * @return {void}
   */
  function prevPane() {
    switchPanes(activePane - 1);
  }

  // TODO: Possibly temporary for plugging the soon-to-be deprecated API.
  function getPanes() {
    return panes;
  }

  /* Initilization code */
  // Make sure to switch to the first pane on init.
  switchPanes(activePane);

  // For a11y with tab functionality, track for focus events on the inputs
  // and switch panes according to which inputs get focused. Don't animate
  // the pane movement though since the tab functionality does it already.
  if (progressOnTab) {
    panes.forEach(pane => {
      const paneNum = parseInt(pane.getAttribute('data-pane'), 10);
      // Use focus with useCapture and take advantage of event delegation to
      // check if there are focusable elements in children being given focus
      // by keyboard.
      pane.addEventListener('focus', () => switchPanes(paneNum), true);
    });
  }

  return {
    getActivePane,
    reset,
    nextPane,
    prevPane,
    switchPanes,
    getPanes,
    setFirstFocus,
  };
}
